import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check, RotateCcw } from "lucide-react";

const SignaturePad = ({ onSave, onClear, width = 340, height = 160 }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // Set up canvas
    canvas.width = width * 2;
    canvas.height = height * 2;
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2.5;
    // Fill background
    ctx.fillStyle = "#0D0E14";
    ctx.fillRect(0, 0, width, height);
    // Draw signature line
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, height - 30);
    ctx.lineTo(width - 20, height - 30);
    ctx.stroke();
    // Draw "Sign here" text
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.font = "12px sans-serif";
    ctx.fillText("Sign here", 20, height - 12);
    // Reset for drawing
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2.5;
  }, [width, height]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0D0E14";
    ctx.fillRect(0, 0, width, height);
    // Redraw line
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, height - 30);
    ctx.lineTo(width - 20, height - 30);
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.font = "12px sans-serif";
    ctx.fillText("Sign here", 20, height - 12);
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2.5;
    setHasDrawn(false);
    if (onClear) onClear();
  };

  const getSignatureData = () => {
    if (!hasDrawn) return null;
    return canvasRef.current.toDataURL("image/png");
  };

  const handleSave = () => {
    const data = getSignatureData();
    if (data && onSave) onSave(data);
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0D0E14]">
        <canvas
          ref={canvasRef}
          style={{ width: `${width}px`, height: `${height}px`, touchAction: "none", cursor: "crosshair" }}
          className="w-full"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearCanvas}
          className="flex-1 border-white/10 text-gray-400 hover:text-white hover:bg-white/5 h-9"
          data-testid="signature-clear-btn"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!hasDrawn}
          className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black h-9 font-semibold disabled:opacity-30"
          data-testid="signature-confirm-btn"
        >
          <Check className="w-3.5 h-3.5 mr-1.5" />
          Confirm Signature
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
